package dev.akshaychordiya.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.HeadlineTextStyle
import dev.akshaychordiya.MetaTextStyle
import dev.akshaychordiya.SectionTitleStyle
import dev.akshaychordiya.SubheadlineTextStyle
import dev.akshaychordiya.components.layouts.PageLayoutData
import dev.akshaychordiya.components.widgets.MapPoint
import dev.akshaychordiya.components.widgets.Preview
import dev.akshaychordiya.components.widgets.WorldMap
import dev.akshaychordiya.content.Talk
import dev.akshaychordiya.content.TALKS_PLAYLIST_URL
import dev.akshaychordiya.content.talks
import dev.akshaychordiya.fadeIn
import dev.akshaychordiya.fadeUp
import dev.akshaychordiya.smoothTransition
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initTalksPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Talks", wide = true))
}

/**
 * What the talk list is currently narrowed to.
 *
 * There are two ways in - the country chips and the map dots - so the filter has to express both rather than being a
 * single nullable country string.
 */
sealed interface TalkFilter {
    data object All : TalkFilter
    data class Country(val name: String) : TalkFilter
    data class City(val name: String) : TalkFilter

    fun accepts(talk: Talk): Boolean = when (this) {
        All -> true
        is Country -> talk.city?.country == name
        is City -> talk.city?.name == name
    }

    /** Whether a plotted city should render as included. */
    fun includes(city: dev.akshaychordiya.content.City): Boolean = when (this) {
        All -> true
        is Country -> city.country == name
        is City -> city.name == name
    }
}

val ChipStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = 0.4.cssRem, leftRight = 0.9.cssRem)
            .borderRadius(2.cssRem)
            .fontSize(0.88.cssRem)
            .cursor(Cursor.Pointer)
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().subdued)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .userSelect(UserSelect.None)
            .smoothTransition("background-color", "color", "border-color", "transform")
    }
    // Excludes the selected chip explicitly, rather than leaving it to the cascade. A selected chip is *filled* with
    // brandPrimary, so a plain `hover` recolouring the label to brandPrimary rendered it green-on-green and
    // unreadable - and `.chip:hover` outranks `.chip-selected`, so the selected colours lost. `:not()` means this
    // rule never matches a selected chip at all, which doesn't depend on which stylesheet is registered last.
    cssRule(":hover:not(.chip-selected)") {
        Modifier
            .color(colorMode.toSitePalette().brandPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary)
    }
    // A tiny press-down gives the filter a physical feel without moving the layout.
    active { Modifier.scale(0.96) }
}

val ChipSelectedStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(colorMode.toSitePalette().brandPrimary)
            // The brand green is dark in light mode and light in dark mode, so the label has to flip with it.
            .color(if (colorMode == ColorMode.LIGHT) Colors.White else Colors.Black)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary)
    }
    // Keeps the flipped label - the whole point of the fix above - and softens the fill slightly so an already-active
    // chip still acknowledges the pointer. background-color is already in the base transition, so it eases.
    hover {
        Modifier
            .backgroundColor(colorMode.toSitePalette().brandPrimary.toRgb().copyf(alpha = 0.85f))
            .color(if (colorMode == ColorMode.LIGHT) Colors.White else Colors.Black)
    }
}

val TalkCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 1.25.cssRem, leftRight = 0.75.cssRem)
            .margin(leftRight = (-0.75).cssRem)
            .borderRadius(0.6.cssRem)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .smoothTransition("background-color")
    }
    hover { Modifier.backgroundColor(colorMode.toSitePalette().surface) }
}

/** Thumbnail above the text on a phone, beside it once there's room. */
val TalkLayoutStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(1.cssRem)
    }
    Breakpoint.SM { Modifier.flexDirection(FlexDirection.Row) }
}

/** The "Watch" / "Slides" links on a talk. The arrow slides out on hover to signal it leaves the page. */
val TalkLinkStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.9.cssRem)
            .fontWeight(500)
            .color(colorMode.toSitePalette().brandPrimary)
            .smoothTransition("gap")
            .gap(0.3.cssRem)
            .display(DisplayStyle.LegacyInlineFlex)
            .alignItems(AlignItems.Center)
    }
    hover { Modifier.gap(0.6.cssRem) }
}

@Composable
private fun Chip(label: String, selected: Boolean, onClick: () -> Unit) {
    Div(
        ChipStyle.toModifier()
            .thenIfSelected(selected)
            .onClick { onClick() }
            .attrsModifier {
                attr("role", "button")
                attr("tabindex", "0")
                attr("aria-pressed", selected.toString())
            }
            .toAttrs()
    ) {
        SpanText(label)
    }
}

@Composable
private fun Modifier.thenIfSelected(selected: Boolean) =
    if (selected) this.then(ChipSelectedStyle.toModifier()) else this

@Composable
private fun TalkRow(talk: Talk, index: Int) {
    val palette = ColorMode.current.toSitePalette()
    Div(TalkCardStyle.toModifier().fadeUp(index).toAttrs()) {
        // Stacks on a phone, thumbnail-beside-text from tablet up - see PreviewWideStyle.
        Div(TalkLayoutStyle.toAttrs()) {
        talk.thumbnailUrl?.let { Preview(it, "Still from ${talk.title}") }
        Column(Modifier.fillMaxWidth().gap(0.4.cssRem)) {
            SpanText(
                talk.title,
                Modifier.fontSize(1.15.cssRem).fontWeight(600).lineHeight(1.35),
            )
            Row(Modifier.gap(0.5.cssRem).flexWrap(FlexWrap.Wrap)) {
                SpanText(
                    "${talk.kind.label} · ${talk.event} · ${talk.date}",
                    MetaTextStyle.toModifier(),
                )
                talk.city?.let {
                    SpanText("· ${it.name}, ${it.country}", MetaTextStyle.toModifier())
                }
                talk.coSpeaker?.let { co ->
                    // Wrapped in one span so "· with Tasha Ramesh" stays a single flex item: as separate children the
                    // Row's 0.5rem gap would open up between "with" and the name.
                    Span(attrs = MetaTextStyle.toAttrs()) {
                        Text("· with ")
                        if (co.profileUrl != null) {
                            // Left underlined rather than undecorated - it leaves the site, and a co-presenter credit
                            // is worth looking like a link.
                            Link(
                                path = co.profileUrl,
                                text = co.name,
                                modifier = Modifier.color(palette.brandPrimary),
                                variant = UncoloredLinkVariant,
                            )
                        } else {
                            Text(co.name)
                        }
                    }
                }
            }
            if (talk.description.isNotEmpty()) {
                SpanText(
                    talk.description,
                    Modifier.fontSize(0.95.cssRem).color(palette.subdued).margin(top = 0.2.cssRem),
                )
            }
            Row(Modifier.gap(1.25.cssRem).margin(top = 0.35.cssRem)) {
                talk.videoId?.let {
                    Link(
                        path = "https://www.youtube.com/watch?v=$it",
                        modifier = TalkLinkStyle.toModifier(),
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    ) {
                        SpanText("Watch")
                        SpanText("→")
                    }
                }
                talk.slidesUrl?.let {
                    Link(
                        path = it,
                        modifier = TalkLinkStyle.toModifier(),
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    ) {
                        SpanText("Slides")
                        SpanText("→")
                    }
                }
            }
        }
        }
    }
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun TalksPage() {
    var filter: TalkFilter by remember { mutableStateOf(TalkFilter.All) }

    val located = talks.filter { it.city != null }
    val countries = located.mapNotNull { it.city?.country }.distinct().sorted()
    val cityCount = located.mapNotNull { it.city?.name }.distinct().size

    val visibleTalks = talks.filter { filter.accepts(it) }

    // Group by city so a place visited five times is one dot, sized accordingly.
    val points = located
        .groupBy { it.city!!.name }
        .map { (_, cityTalks) ->
            val city = cityTalks.first().city!!
            MapPoint(
                city = city,
                count = cityTalks.size,
                highlighted = filter.includes(city),
                selected = filter == TalkFilter.City(city.name),
            )
        }

    Column(
        Modifier.fillMaxWidth().gap(1.cssRem).margin(top = 1.5.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Div(SectionTitleStyle.toModifier().fadeIn(index = 0).toAttrs()) { SpanText("Speaking globally") }

        Div(HeadlineTextStyle.toModifier().textAlign(TextAlign.Center).fadeUp(index = 1).toAttrs()) {
            SpanText("${talks.size} talks. $cityCount cities. ${countries.size} countries.")
        }

        Div(SubheadlineTextStyle.toModifier().textAlign(TextAlign.Center).fadeUp(index = 2).toAttrs()) {
            SpanText("From local meetups to droidcon stages, including a keynote.")
        }

        Row(
            Modifier
                .gap(0.5.cssRem)
                .margin(top = 1.cssRem)
                .flexWrap(FlexWrap.Wrap)
                .justifyContent(JustifyContent.Center)
                .fadeUp(index = 3),
        ) {
            Chip("All", filter == TalkFilter.All) { filter = TalkFilter.All }
            countries.forEach { country ->
                val chipFilter = TalkFilter.Country(country)
                Chip(country, filter == chipFilter) { filter = chipFilter }
            }
            // Picking a dot narrows further than any chip can, so surface it as its own chip - otherwise the only
            // clue about why the list shrank is a small ring on the map, and there'd be no obvious way back out.
            (filter as? TalkFilter.City)?.let { city ->
                Chip("${city.name} ✕", selected = true) { filter = TalkFilter.All }
            }
        }

        WorldMap(points, Modifier.margin(top = 1.5.cssRem)) { city ->
            // Clicking the active city again clears the filter rather than doing nothing.
            filter = if (filter == TalkFilter.City(city.name)) TalkFilter.All else TalkFilter.City(city.name)
        }

        Link(
            path = TALKS_PLAYLIST_URL,
            modifier = TalkLinkStyle.toModifier().margin(top = 0.5.cssRem).fadeIn(index = 4),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        ) {
            SpanText("Watch every talk on YouTube")
            SpanText("→")
        }

        Column(Modifier.fillMaxWidth().margin(top = 2.cssRem)) {
            visibleTalks.forEachIndexed { index, talk -> TalkRow(talk, index) }
        }
    }
}
